// src/products/dataroom/utils/slugUtils.js
// Utility functions for generating and working with URL slugs and tokens

/**
 * Generate a random 5-character alphanumeric token
 * @returns {string} - 5 character token (letters and numbers)
 */
export const generateToken = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let token = '';
  for (let i = 0; i < 5; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

/**
 * Validate and clean URL name (alphanumeric only, max 20 chars)
 * @param {string} name - The URL name input
 * @returns {string} - Cleaned URL name
 */
export const cleanUrlName = (name) => {
  if (!name) return '';
  
  // Remove all non-alphanumeric characters and limit to 20 chars
  return name
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 20)
    .toLowerCase();
};

/**
 * Get project URL identifier from project object
 * Format: url_slug-url_token (e.g., "mydealroom-a3b2c")
 * @param {object} project - Project object with url_slug and url_token
 * @returns {string} - URL identifier or fallback to ID
 */
export const getProjectSlug = (project) => {
  if (!project) return '';
  
  // Use url_slug + url_token if available
  if (project.url_slug && project.url_token) {
    return `${project.url_slug}-${project.url_token}`;
  }
  
  // Fallback to ID for backward compatibility
  return project.id?.toString() || '';
};

/**
 * Find project by URL identifier (slug-token) or ID
 * @param {array} projects - Array of project objects
 * @param {string} identifier - URL identifier (slug-token) or numeric ID
 * @returns {object|null} - Matching project or null
 */
export const findProjectBySlugOrId = (projects, identifier) => {
  if (!projects || !identifier) return null;
  
  // First try to find by ID (numeric) - backward compatibility
  const numericId = parseInt(identifier, 10);
  if (!isNaN(numericId) && numericId > 0) {
    const byId = projects.find(p => p.id === numericId);
    if (byId) return byId;
  }
  
  // Try to find by url_slug-url_token format
  // Format: "slug-token" where token is exactly 5 chars (e.g., "mydealroom-a3b2c")
  if (identifier.includes('-')) {
    const parts = identifier.split('-');
    if (parts.length >= 2) {
      // Last part should be the 5-char token
      const lastPart = parts[parts.length - 1];
      if (lastPart.length === 5) {
        const token = lastPart;
        const slug = parts.slice(0, -1).join('-');
        
        const byUrl = projects.find(p => 
          p.url_slug === slug && p.url_token === token
        );
        if (byUrl) return byUrl;
      }
    }
  }
  
  // Try to find by url_slug only (in case token is missing or format is different)
  const bySlug = projects.find(p => p.url_slug === identifier);
  if (bySlug) return bySlug;
  
  return null;
};

/**
 * Check if URL name is available
 * @param {string} urlName - The URL name to check
 * @param {array} existingProjects - Array of existing projects
 * @param {number} excludeProjectId - Project ID to exclude from check (for updates)
 * @returns {boolean} - True if available
 */
export const isUrlNameAvailable = (urlName, existingProjects, excludeProjectId = null) => {
  if (!urlName) return false;
  
  const cleaned = cleanUrlName(urlName);
  if (!cleaned) return false;
  
  // Check if any project (except excluded) has this url_slug
  return !existingProjects.some(p => {
    if (excludeProjectId && p.id === excludeProjectId) return false;
    return p.url_slug === cleaned;
  });
};

